import React, { useEffect, useState } from 'react';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { Body, SectionHeader } from '@grnhse/seedling/lib/birch/typography';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { TrackLastPage } from './shared/track_last_page';
import MultiStepDataEntryModal from '@grnhse/seedling/lib/birch/components/modals/multi_step_data_entry_modal';
import {
  standardRequestWithErrorFlash,
  standardRequestWithFlash,
} from 'shared/utils/request';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { withLocalePrefix, tList } from 'shared/utils/translation';
import SelectJobStageFields from './shared/select_job_stage_fields';
import {
  InformationalCallOut,
  WarningCallOut,
} from '@grnhse/seedling/lib/birch/components/call_out';
import { useCandidateProfile } from 'candidate_profile_redesign/store';
import { SimpleFormSingleDropdown } from '../shared/form/Fields';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { RadioGroupField } from '@grnhse/seedling/lib/birch/components/forms/fields/radio_group_field';

const t = withLocalePrefix('candidate_profile_redesign.modals.transfer_job');
const transferDetails = tList(
  'candidate_profile_redesign.modals.transfer_job.transfer_details'
);
const destroyDetails = tList(
  'candidate_profile_redesign.modals.transfer_job.destroy_details'
);

interface ModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  hiringPlansPath: string;
  ownerOptionsPath: string;
  submitPath: string;
}

interface FormProps {
  hiring_plan: Item;
  stage: Item;
  recruiter: Item | null;
  coordinator: Item | null;
  change_or_keep_application_date: 'keep' | 'change';
  isLastPage: boolean;
  responsibilitySectionLoaded: boolean;
}

const CalloutBody = ({ bulletList }: { bulletList: Array<string> }) => (
  <ul
    style={{
      paddingInlineStart: '22px',
      marginBlockStart: '4px',
      marginBlockEnd: 0,
    }}
  >
    {bulletList.map((bullet, index) => (
      <li key={index}>
        <Body>{bullet}</Body>
      </li>
    ))}
  </ul>
);

interface OwnerOptionsResponse {
  recruiterOptions: Item[];
  coordinatorOptions: Item[];
}

const ReassignResponsibilitySection = ({
  ownerOptionsPath,
}: {
  ownerOptionsPath: string;
}) => {
  const { setValue, watch } = useFormContext();
  const hiringPlan: Item = watch('hiring_plan');
  const [recruiterOptions, setRecruiterOptions] = useState<Item[]>([]);
  const [coordinatorOptions, setCoordinatorOptions] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);

  const firstOwner = (ownerOptions: Item[]) => {
    return ownerOptions.length > 0 ? ownerOptions[0] : undefined;
  };

  useEffect(() => {
    const handleJobChange = async (jobId: string) => {
      const response =
        await standardRequestWithErrorFlash.get<OwnerOptionsResponse>(
          `${ownerOptionsPath}?new_hiring_plan_id=${jobId}`
        );

      setRecruiterOptions(response.data.recruiterOptions);
      setCoordinatorOptions(response.data.coordinatorOptions);
      setLoading(false);
      setValue('recruiter', firstOwner(response.data.recruiterOptions));
      setValue('coordinator', firstOwner(response.data.coordinatorOptions));
      setValue('responsibilitySectionLoaded', true);
    };

    setValue('responsibilitySectionLoaded', false);

    if (hiringPlan) {
      setLoading(true);
      handleJobChange(hiringPlan.value);
    } else {
      setRecruiterOptions([]);
      setCoordinatorOptions([]);
      setValue('recruiter', undefined);
      setValue('coordinator', undefined);
    }
  }, [hiringPlan, setValue, ownerOptionsPath]);

  if (loading) {
    return <SkeletonLoader rows={3} columnsPerRow={1} />;
  }

  if (
    !hiringPlan ||
    (recruiterOptions.length === 0 && coordinatorOptions.length === 0)
  ) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <SectionHeader>{t('reassign_responsibility')}</SectionHeader>

      {recruiterOptions.length > 0 && (
        <SimpleFormSingleDropdown
          name="recruiter"
          label={t('reassign_recruiter')}
          items={recruiterOptions}
          clearable={true}
          isPiiBlurred
        />
      )}

      {coordinatorOptions.length > 0 && (
        <SimpleFormSingleDropdown
          name="coordinator"
          label={t('reassign_coordinator')}
          items={coordinatorOptions}
          clearable={true}
          isPiiBlurred
        />
      )}
    </Stack>
  );
};

const KeepApplicationDate = () => {
  const options: Array<{ label: string; value: string }> = [
    { label: t('application_date.keep_current_date'), value: 'keep' },
    { label: t('application_date.change_to_today'), value: 'change' },
  ];

  return (
    <RadioGroupField
      name={'change_or_keep_application_date'}
      ariaLabel={t('application_date.header')}
      data-provides={'change-or-keep-application-date'}
      options={options}
    />
  );
};

const PageOne = ({
  hiringPlansPath,
  ownerOptionsPath,
  jobName,
}: {
  hiringPlansPath?: string;
  ownerOptionsPath: string;
  jobName: string;
}) => (
  <MultiStepDataEntryModal.Page>
    <Stack spacing={3}>
      <InformationalCallOut
        bodyText={<CalloutBody bulletList={transferDetails} />}
        headerText={t('transfer_details_header')}
      />
      <Stack spacing={1}>
        <SectionHeader>{t('current_job')}</SectionHeader>
        <Body>{jobName}</Body>
      </Stack>
      <Stack spacing={1}>
        <SectionHeader>{t('transfer_to')}</SectionHeader>
        <SelectJobStageFields hiringPlansPath={hiringPlansPath} />
      </Stack>
      <Stack spacing={1}>
        <SectionHeader>{t('application_date.header')}</SectionHeader>
        <KeepApplicationDate />
      </Stack>
      <ReassignResponsibilitySection ownerOptionsPath={ownerOptionsPath} />
    </Stack>
  </MultiStepDataEntryModal.Page>
);

const PageTwo = ({ jobName }: { jobName: string }) => {
  const { getValues } = useFormContext();

  const recruiter = getValues('recruiter');
  const coordinator = getValues('coordinator');
  const keepApplicationDate = getValues('change_or_keep_application_date');

  // A value of undefined means we did not surface the Reassign Responsibility section,
  // or we showed the section but not that specific dropdown because it had no options available.
  // A value of null means we surfaced the section and dropdown, but the user cleared it out.
  const displayRecruiter = recruiter !== undefined;
  const displayCoordinator = coordinator !== undefined;

  return (
    <MultiStepDataEntryModal.Page>
      <Stack spacing={3}>
        <WarningCallOut
          bodyText={<CalloutBody bulletList={destroyDetails} />}
          headerText={t('destroy_details_header')}
        />
        <Stack spacing={1}>
          <SectionHeader>{t('current_job')}</SectionHeader>
          <Body>{jobName}</Body>
        </Stack>

        <Stack spacing={1}>
          <SectionHeader>{t('transfer_to')}</SectionHeader>
          <Body>{getValues('hiring_plan').label}</Body>
          <Body>{t('in_stage', { stage: getValues('stage').label })}</Body>
        </Stack>

        <Stack spacing={1}>
          <SectionHeader>{t('application_date.header')}</SectionHeader>
          <Body>
            {keepApplicationDate === 'keep'
              ? t('application_date.keep_current_date')
              : t('application_date.change_to_today')}
          </Body>
        </Stack>

        {(displayRecruiter || displayCoordinator) && (
          <Stack spacing={1}>
            <SectionHeader>{t('reassign_responsibility')}</SectionHeader>
            {displayRecruiter && (
              <Body className="pii-blur">
                {recruiter?.label || t('no_recruiter')}
              </Body>
            )}
            {displayCoordinator && (
              <Body className="pii-blur">
                {coordinator?.label || t('no_coordinator')}
              </Body>
            )}
          </Stack>
        )}
      </Stack>
    </MultiStepDataEntryModal.Page>
  );
};

const ModalFooter = ({
  onDismiss,
  submitting,
}: {
  onDismiss: () => void;
  submitting: boolean;
}) => {
  const {
    formState: { isValid },
    watch,
  } = useFormContext();

  const responsibilitySectionLoaded = watch('responsibilitySectionLoaded');

  return (
    <MultiStepDataEntryModal.Footer>
      {({
        isFirstPage,
        isLastPage,
      }: {
        isFirstPage: boolean;
        isLastPage: boolean;
      }) => (
        <Grid
          container
          item
          spacing={2}
          justify="space-between"
          alignItems={'center'}
          wrap={'nowrap'}
        >
          <TrackLastPage isLastPage={isLastPage} />
          <Grid item justify="flex-start">
            {!isFirstPage && (
              <MultiStepDataEntryModal.BackButton>
                {t('back')}
              </MultiStepDataEntryModal.BackButton>
            )}
          </Grid>
          <Grid item>
            <CompactRow>
              <MultiStepDataEntryModal.CancelButton onCancel={onDismiss}>
                {t('cancel')}
              </MultiStepDataEntryModal.CancelButton>
              {isLastPage ? (
                <MultiStepDataEntryModal.SaveButton
                  disabled={submitting || !isValid}
                  loading={submitting}
                >
                  {t('submit')}
                </MultiStepDataEntryModal.SaveButton>
              ) : (
                <MultiStepDataEntryModal.NextButton
                  disabled={!isValid || !responsibilitySectionLoaded}
                >
                  {t('next')}
                </MultiStepDataEntryModal.NextButton>
              )}
            </CompactRow>
          </Grid>
        </Grid>
      )}
    </MultiStepDataEntryModal.Footer>
  );
};

const TransferJobModal = ({
  isOpen,
  onDismiss,
  hiringPlansPath,
  ownerOptionsPath,
  submitPath,
}: ModalProps) => {
  const {
    state: {
      header: { breadcrumbsLabel },
    },
  } = useCandidateProfile();

  const [submitting, setSubmitting] = useState(false);

  const onTransferJobSave = async (form: FormProps) => {
    setSubmitting(true);

    const payload = new FormData();
    payload.append('hiring_plan_id', form.hiring_plan.value);
    payload.append('initial_stage_id', form.stage.value);
    payload.append('recruiter_id', form.recruiter?.value || '');
    payload.append('coordinator_id', form.coordinator?.value || '');
    payload.append(
      'change_or_keep_application_date',
      form.change_or_keep_application_date
    );
    payload.append('type', 'move');

    try {
      const { data: submitResponse } = await standardRequestWithFlash.post(
        submitPath,
        payload
      );
      onDismiss();
      if (submitResponse.goto) {
        window.location.href = submitResponse.goto;
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      onSubmit={onTransferJobSave}
      defaultValues={{
        change_or_keep_application_date: 'keep',
      }}
    >
      <MultiStepDataEntryModal
        size={'medium'}
        isOpen={isOpen}
        onDismiss={onDismiss}
        onSubmit={() => null}
        ariaLabel={'transfer-job-modal'}
        data-provides={'transfer-job-modal'}
        numberOfPages={2}
      >
        <MultiStepDataEntryModal.Header onClick={onDismiss}>
          {t('header')}
        </MultiStepDataEntryModal.Header>

        <MultiStepDataEntryModal.Body>
          <PageOne
            hiringPlansPath={hiringPlansPath}
            ownerOptionsPath={ownerOptionsPath}
            jobName={breadcrumbsLabel}
          />
          <PageTwo jobName={breadcrumbsLabel} />
        </MultiStepDataEntryModal.Body>

        <ModalFooter onDismiss={onDismiss} submitting={submitting} />
      </MultiStepDataEntryModal>
    </Form>
  );
};

export default TransferJobModal;
